import { readClaims, type SagotClient, type SessionSnapshot } from "@sagot/api-client";
import { IMPERSONATION_HASH_PARAM } from "@sagot/constants";
import { createContext, useCallback, useContext, useEffect, useState, useSyncExternalStore, type ReactNode } from "react";

interface SessionContextValue {
  client: SagotClient;
  /** True when the boot refresh failed for a transport reason (not "signed out"). */
  bootFailed: boolean;
  retryBoot: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

/** Adopts an admin "view as tenant" token passed in the URL fragment, then strips it. */
function adoptImpersonationToken(client: SagotClient): boolean {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const token = params.get(IMPERSONATION_HASH_PARAM);
  if (!token) return false;
  window.history.replaceState(null, "", window.location.pathname + window.location.search);
  const claims = readClaims(token);
  if (!claims?.imp || !claims.exp) return false;
  const expiresIn = Math.max(claims.exp - Math.floor(Date.now() / 1000), 1);
  client.session.setSession({ access_token: token, expires_in: expiresIn }, { ephemeral: true });
  return true;
}

export function SessionProvider({ client, children }: { client: SagotClient; children: ReactNode }) {
  const [bootFailed, setBootFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (attempt === 0 && adoptImpersonationToken(client)) return;
    if (client.session.snapshot().status !== "unknown") return;
    client.session.restore().then(
      () => {
        setBootFailed(false);
      },
      () => {
        setBootFailed(true);
      },
    );
  }, [client, attempt]);

  const retryBoot = useCallback(() => {
    setBootFailed(false);
    setAttempt((n) => n + 1);
  }, []);

  return <SessionContext.Provider value={{ client, bootFailed, retryBoot }}>{children}</SessionContext.Provider>;
}

function useSessionContext(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("SessionProvider is missing");
  return value;
}

export function useClient(): SagotClient {
  return useSessionContext().client;
}

export function useApi(): SagotClient["api"] {
  return useSessionContext().client.api;
}

export function useBootState(): { failed: boolean; retry: () => void } {
  const { bootFailed, retryBoot } = useSessionContext();
  return { failed: bootFailed, retry: retryBoot };
}

/** Re-renders on sign-in, refresh and sign-out (in this or a sibling tab). */
export function useSession(): SessionSnapshot {
  const { client } = useSessionContext();
  return useSyncExternalStore(
    (onChange) => client.session.subscribe(onChange),
    () => client.session.snapshot(),
  );
}
